import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Landmark, Coins, Pencil, ArrowLeftRight, ArrowRight, Ban } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PageHeader, GlassCard, GradButton, EmptyState, Field, fmtMoney } from "@/components/erp";
import { SelectBox } from "@/components/Inputs";
import { hesapOnbellekSifirla } from "@/components/HesapSec";
import { IbanInput, BankaInput, SubeInput } from "@/components/BankaAlanlari";
import { useAuth } from "@/context/AuthContext";
import { can, PARA } from "@/lib/perms";
import api, { errMsg } from "@/lib/api";

const BOS = { ad: "", tip: "banka", banka: "", sube: "", iban: "", para_birimi: "TRY", acilis_bakiye: 0, aktif: true };

const HesapDialog = ({ h, onClose, onSaved }) => {
  const [f, setF] = useState(h || BOS);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    try {
      const body = { ...f, acilis_bakiye: Number(f.acilis_bakiye || 0) };
      if (h) await api.put(`/hesaplar/${h.id}`, body); else await api.post("/hesaplar", body);
      hesapOnbellekSifirla();
      toast.success("Hesap kaydedildi");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass max-w-lg" data-testid="hesap-dialog">
        <DialogHeader><DialogTitle className="font-head">{h ? "Hesabı düzenle" : "Yeni banka / kasa hesabı"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Hesap adı *" className="sm:col-span-2"><Input data-testid="hesap-ad" className="bg-white/5" value={f.ad} onChange={(e) => set("ad", e.target.value)} /></Field>
          <Field label="Tip"><SelectBox testid="hesap-tip" value={f.tip} onChange={(v) => set("tip", v)} options={[{ value: "banka", label: "Banka" }, { value: "kasa", label: "Kasa" }]} /></Field>
          <Field label="Para birimi"><SelectBox testid="hesap-para" value={f.para_birimi} onChange={(v) => set("para_birimi", v)} options={PARA} /></Field>
          {f.tip === "banka" && <>
            <Field label="Banka"><BankaInput testid="hesap-banka" value={f.banka} onChange={(v) => set("banka", v)} /></Field>
            <Field label="Şube"><SubeInput testid="hesap-sube" banka={f.banka} value={f.sube} onChange={(v) => set("sube", v)} /></Field>
            <Field label="IBAN" className="sm:col-span-2"><IbanInput testid="hesap-iban" value={f.iban} onChange={(v) => set("iban", v)} onBanka={(b) => !f.banka && set("banka", b)} /></Field>
          </>}
          <Field label="Açılış bakiyesi"><Input data-testid="hesap-acilis" type="number" step="any" className="bg-white/5" value={f.acilis_bakiye} onChange={(e) => set("acilis_bakiye", e.target.value)} /></Field>
          <label className="flex items-end gap-2 text-sm pb-2"><Switch data-testid="hesap-aktif" checked={f.aktif} onCheckedChange={(v) => set("aktif", v)} />Aktif</label>
        </div>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="px-5 py-2 rounded-full border border-white/10 text-sm" data-testid="hesap-vazgec">Vazgeç</button><GradButton onClick={save} data-testid="hesap-kaydet">Kaydet</GradButton></div>
      </DialogContent>
    </Dialog>
  );
};

const Hareketler = ({ id, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/hesaplar/${id}/hareketler`).then((r) => setD(r.data)); }, [id]);
  if (!d) return null;
  const cur = d.hesap.para_birimi;
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="glass w-full sm:max-w-2xl overflow-y-auto erp-scroll" data-testid="hesap-hareketler">
        <SheetHeader><SheetTitle className="font-head text-2xl">{d.hesap.ad}</SheetTitle>
          <div className="text-sm text-muted-foreground">{d.hesap.banka}{d.hesap.iban ? ` · ${d.hesap.iban}` : ""} · açılış {fmtMoney(d.acilis_bakiye, cur)}</div></SheetHeader>
        {d.hareketler.length === 0 ? <EmptyState text="Bu hesapta hareket yok." testid="hareket-bos" /> : (
          <table className="w-full text-sm mt-5">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-white/5"><th className="p-3">Tarih</th><th className="p-3">Açıklama</th><th className="p-3 text-right">Tutar</th><th className="p-3 text-right">Bakiye</th></tr></thead>
            <tbody>{d.hareketler.map((x, i) => (
              <tr key={i} className="border-b border-white/5" data-testid={`hareket-${x.belge}`}>
                <td className="p-3 text-xs">{x.tarih}</td><td className="p-3 text-xs"><span className="font-mono mr-2">{x.belge}</span>{x.aciklama}</td>
                <td className={`p-3 text-right font-mono ${x.tutar < 0 ? "text-red-300" : "text-emerald-300"}`}>{fmtMoney(x.tutar, cur)}</td>
                <td className="p-3 text-right font-mono">{fmtMoney(x.bakiye, cur)}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </SheetContent>
    </Sheet>
  );
};

const VirmanDialog = ({ hesaplar, onClose, onSaved }) => {
  const aktif = hesaplar.filter((h) => h.aktif);
  const [f, setF] = useState({ kaynak_hesap_id: "", hedef_hesap_id: "", cikan_tutar: "", giren_tutar: "", tarih: new Date().toLocaleDateString("sv-SE"), aciklama: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const k = aktif.find((h) => h.id === f.kaynak_hesap_id);
  const h = aktif.find((x) => x.id === f.hedef_hesap_id);
  const doviz = k && h && k.para_birimi !== h.para_birimi;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/virmanlar", { ...f, cikan_tutar: Number(f.cikan_tutar), giren_tutar: doviz ? Number(f.giren_tutar) : null });
      toast.success(`${data.no} · ${data.kaynak_ad} → ${data.hedef_ad}`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const ops = (haric) => aktif.filter((x) => x.id !== haric).map((x) => ({ value: x.id, label: `${x.ad} · ${fmtMoney(x.bakiye, x.para_birimi)}` }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass max-w-lg" data-testid="virman-dialog">
        <DialogHeader><DialogTitle className="font-head">Hesaplar arası virman</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Çıkan hesap *"><SelectBox testid="virman-kaynak" value={f.kaynak_hesap_id} onChange={(v) => set("kaynak_hesap_id", v)} options={ops(f.hedef_hesap_id)} /></Field>
          <Field label="Giren hesap *"><SelectBox testid="virman-hedef" value={f.hedef_hesap_id} onChange={(v) => set("hedef_hesap_id", v)} options={ops(f.kaynak_hesap_id)} /></Field>
          <Field label={`Çıkan tutar${k ? ` (${k.para_birimi})` : ""} *`}><Input data-testid="virman-cikan" type="number" step="any" className="bg-white/5" value={f.cikan_tutar} onChange={(e) => set("cikan_tutar", e.target.value)} /></Field>
          {doviz && <Field label={`Giren tutar (${h.para_birimi}) *`}><Input data-testid="virman-giren" type="number" step="any" className="bg-white/5" value={f.giren_tutar} onChange={(e) => set("giren_tutar", e.target.value)} /></Field>}
          <Field label="Tarih"><Input data-testid="virman-tarih" type="date" max={new Date().toLocaleDateString("sv-SE")} className="bg-white/5" value={f.tarih} onChange={(e) => set("tarih", e.target.value)} /></Field>
          <Field label="Açıklama" className="sm:col-span-2"><Input data-testid="virman-aciklama" className="bg-white/5" value={f.aciklama} onChange={(e) => set("aciklama", e.target.value)} /></Field>
        </div>
        {doviz && Number(f.cikan_tutar) > 0 && Number(f.giren_tutar) > 0 && <div className="text-xs text-muted-foreground" data-testid="virman-kur">Döviz bozdurma · 1 {k.para_birimi} = {(Number(f.giren_tutar) / Number(f.cikan_tutar)).toLocaleString("tr-TR", { maximumFractionDigits: 4 })} {h.para_birimi}</div>}
        <div className="flex justify-end gap-2"><button onClick={onClose} className="px-5 py-2 rounded-full border border-white/10 text-sm" data-testid="virman-vazgec">Vazgeç</button><GradButton onClick={save} disabled={busy || !k || !h} data-testid="virman-kaydet">Aktar</GradButton></div>
      </DialogContent>
    </Dialog>
  );
};

const VirmanListe = ({ list, yaz, onIptal }) => (
  <GlassCard className="overflow-x-auto mt-6" data-testid="virman-liste">
    <div className="px-4 pt-4 pb-2 text-sm font-medium">Son virmanlar</div>
    <table className="w-full text-sm"><tbody>{list.map((v) => (
      <tr key={v.id} className={`border-t border-white/5 ${v.durum === "iptal" ? "opacity-50" : ""}`} data-testid={`virman-satir-${v.no}`}>
        <td className="p-3 font-mono text-xs">{v.no}{v.durum === "iptal" && <span className="ml-2 text-[10px] text-red-300">iptal</span>}</td><td className="p-3 text-xs">{v.tarih}</td>
        <td className="p-3">{v.kaynak_ad} <ArrowRight className="inline w-3.5 h-3.5 mx-1 text-muted-foreground" /> {v.hedef_ad}<div className="text-xs text-muted-foreground">{v.kaydeden_ad}{v.aciklama ? ` · ${v.aciklama}` : ""}</div></td>
        <td className="p-3 text-right font-mono">{fmtMoney(v.cikan_tutar, v.kaynak_para)}{v.kur ? <div className="text-xs text-muted-foreground">→ {fmtMoney(v.giren_tutar, v.hedef_para)}</div> : null}</td>
        <td className="p-3 text-right">{yaz && v.durum === "aktif" && <button title="İptal" data-testid={`virman-iptal-${v.no}`} onClick={() => onIptal(v)} className="p-2 rounded-lg text-muted-foreground hover:text-red-300"><Ban className="w-4 h-4" /></button>}</td>
      </tr>
    ))}</tbody></table>
  </GlassCard>
);

export default function BankaHesaplari() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [detay, setDetay] = useState(null);
  const [virman, setVirman] = useState(false);
  const [virmanlar, setVirmanlar] = useState([]);
  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get("/hesaplar"), api.get("/virmanlar")]);
    setList(a.data); setVirmanlar(b.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const virmanIptal = async (v) => {
    if (!window.confirm(`${v.no} iptal edilsin mi? Tutar ${v.kaynak_ad} hesabına geri döner.`)) return;
    try { await api.post(`/virmanlar/${v.id}/iptal`); toast.success("Virman iptal edildi"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toplam = list.filter((h) => h.aktif && h.para_birimi === "TRY").reduce((s, h) => s + h.bakiye, 0);
  return (
    <div data-testid="hesaplar-page">
      <PageHeader eyebrow="Finans" title="Banka & Kasa" desc={`Tahsilat ve ödemelerde seçilen hesaplara göre bakiye. Aktif TL hesapları toplamı: ${fmtMoney(toplam)}`}>
        {can(user, "hesap:write") && <div className="flex gap-2">
          {list.filter((h) => h.aktif).length > 1 && <button data-testid="virman-ekle" onClick={() => setVirman(true)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border border-white/10 hover:bg-white/10"><ArrowLeftRight className="w-4 h-4" />Virman</button>}
          <GradButton data-testid="hesap-ekle" onClick={() => setDlg({})}><Plus className="w-4 h-4" />Hesap ekle</GradButton>
        </div>}
      </PageHeader>
      {list.length === 0 ? <GlassCard><EmptyState text="Henüz banka / kasa hesabı yok." testid="hesap-bos" /></GlassCard> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{list.map((h, i) => {
          const Icon = h.tip === "kasa" ? Coins : Landmark;
          return (
            <GlassCard key={h.id} className={`p-5 cursor-pointer hover:-translate-y-0.5 transition-transform ${h.aktif ? "" : "opacity-50"}`} style={{ animationDelay: `${i * 50}ms` }} onClick={() => setDetay(h.id)} data-testid={`hesap-kart-${h.ad}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl grad-bg flex items-center justify-center"><Icon className="w-5 h-5 text-white" /></div>
                  <div><div className="font-medium">{h.ad}</div><div className="text-xs text-muted-foreground">{h.tip === "kasa" ? "Kasa" : h.banka || "Banka"} · {h.para_birimi}</div></div></div>
                {can(user, "hesap:write") && <button onClick={(e) => { e.stopPropagation(); setDlg({ h }); }} data-testid={`hesap-duzenle-${h.ad}`} className="p-2 rounded-lg text-muted-foreground hover:text-white hover:bg-white/10"><Pencil className="w-4 h-4" /></button>}
              </div>
              <div className={`font-head text-2xl mt-5 ${h.bakiye < 0 ? "text-red-300" : ""}`} data-testid={`hesap-bakiye-${h.ad}`}>{fmtMoney(h.bakiye, h.para_birimi)}</div>
              {h.iban && <div className="text-[11px] text-muted-foreground font-mono mt-1 truncate">{h.iban}</div>}
            </GlassCard>
          );
        })}</div>
      )}
      {virmanlar.length > 0 && <VirmanListe list={virmanlar} yaz={can(user, "hesap:write")} onIptal={virmanIptal} />}
      {virman && <VirmanDialog hesaplar={list} onClose={() => setVirman(false)} onSaved={() => { setVirman(false); load(); }} />}
      {dlg && <HesapDialog h={dlg.h} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />}
      {detay && <Hareketler id={detay} onClose={() => setDetay(null)} />}
    </div>
  );
}
